import { Component, OnInit, OnDestroy, signal, output } from '@angular/core';
import { CommonModule } from '@angular/common';
declare const Html5Qrcode: any;
import { FormsModule } from '@angular/forms';

import { NzSelectModule } from 'ng-zorro-antd/select';
import { SafeSelectComponent, SafeSelectOption } from '../safe-select/safe-select';

@Component({
  selector: 'app-custom-scanner',
  imports: [CommonModule, NzSelectModule, FormsModule, SafeSelectComponent],
  templateUrl: './zxing-scanner.html',
  styles: [
    `
      :host {
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        display: block;
        margin: 20px auto;
        padding: 20px;
        background: #f9f9f9;
        border-radius: 12px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
      }
      .header {
        text-align: center;
        margin-bottom: 20px;
      }
      .status-text {
        color: #666;
        font-size: 0.9rem;
      }

      .scanner-viewport {
        position: relative;
        background: #000;
        border-radius: 12px;
        overflow: hidden;
        /* Increased height slightly to ensure visibility */
        min-height: 400px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }

      #reader {
        width: 100%;
        height: 100%;
        min-height: 400px;
        background: #000;
        display: block;
      }

      /* FIX: Target the video element generated by the library */
      #reader video {
        width: 100% !important;
        height: auto !important;
        min-height: 400px;
        object-fit: cover; /* Ensures video fills container without stretching */
      }

      .placeholder {
        color: white;
        text-align: center;
        padding: 20px;
        position: absolute;
        /* Ensure placeholder sits ON TOP of the black reader div */
        z-index: 10;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
      }
      .icon-camera {
        font-size: 4rem;
        margin-bottom: 10px;
        opacity: 0.5;
      }

      .btn-primary {
        background: #007bff;
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 6px;
        font-size: 1rem;
        cursor: pointer;
        font-weight: 600;
        transition: background 0.2s;
      }
      .btn-primary:hover {
        background: #0056b3;
      }

      .btn-secondary {
        position: absolute;
        bottom: 20px;
        z-index: 20;
        background: rgba(255, 255, 255, 0.2);
        color: white;
        border: 1px solid white;
        padding: 8px 16px;
        border-radius: 20px;
        cursor: pointer;
        backdrop-filter: blur(4px);
      }

      .error-banner {
        background: #ffebee;
        color: #c62828;
        padding: 12px;
        border-radius: 6px;
        margin-bottom: 20px;
        border-left: 5px solid #c62828;
      }
      .error-text {
        color: #ff5252;
        margin-top: 10px;
        font-weight: bold;
        background: rgba(0, 0, 0, 0.5);
        padding: 5px;
        border-radius: 4px;
      }

      .controls {
        margin-top: 15px;
        display: flex;
        justify-content: center;
        gap: 10px;
        align-items: center;
      }

      .result-card {
        margin-top: 20px;
        background: #e8f5e9;
        padding: 15px;
        border-radius: 8px;
        border: 1px solid #a5d6a7;
        text-align: center;
      }
      .code-content {
        font-family: monospace;
        font-size: 1.2rem;
        word-break: break-all;
        margin: 10px 0;
      }
    `,
  ],
})
export class ScannerComponent implements OnInit, OnDestroy {
  isCameraActive = signal(false);
  libraryLoaded = signal(false);
  scanResult = signal('');
  statusMessage = signal('Initializing...');
  permissionError = signal('');
  cameras = signal<{ id: string; label: string }[]>([]);
  html5QrCode: any;
  currentCameraId: string | null = null;
  isInsecure = false;
  selectedValue = null;
  devices: any;

  get deviceOptions(): SafeSelectOption[] {
    return (this.devices || []).map((d: any) => ({ value: d.label, label: d.label }));
  }

  scanSuccess = output<any>();

  constructor() {
    this.isInsecure =
      window.location.protocol === 'http:' && window.location.hostname !== 'localhost';
  }

  ngOnInit() {
    this.startScanner();
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/html5-qrcode';
    script.onload = () => {
      this.libraryLoaded.set(true);
      this.statusMessage.set('Library Loaded. Ready.');
    };
    script.onerror = () => {
      this.statusMessage.set('Failed to load scanner library.');
    };
    document.body.appendChild(script);
  }

  ngOnDestroy() {
    if (this.html5QrCode && this.isCameraActive()) {
      this.html5QrCode.stop().catch((err: any) => console.error(err));
    }
  }

  async startScanner() {
    if (!this.libraryLoaded()) return;

    this.permissionError.set('');
    this.statusMessage.set('Requesting Camera...');

    try {
      // 1. Get Cameras
      const devices = await Html5Qrcode.getCameras();
      this.devices = devices;

      if (devices && devices.length) {
        this.cameras.set(devices);

        // Try to find back camera
        const backCam = devices.find(
          (d: any) =>
            d.label.toLowerCase().includes('back') || d.label.toLowerCase().includes('environment')
        );
        this.currentCameraId = backCam ? backCam.id : devices[0].id;

        // FIX: Set camera active TRUE here so the UI updates and the #reader div is ready/visible
        this.isCameraActive.set(true);

        this.startScanningWithId(this.currentCameraId!);
      } else {
        this.permissionError.set('No cameras found.');
      }
    } catch (err: any) {
      console.error(err);
      this.permissionError.set('Camera permission denied or error occurred.');
      this.statusMessage.set('Error');
    }
  }

  startScanningWithId(cameraId: string) {
    if (!this.html5QrCode) {
      this.html5QrCode = new Html5Qrcode('reader');
    }

    this.html5QrCode
      .start(
        cameraId,
        {
          fps: 10, // Optional, frame per seconds for qr code scanning
          // Setting the container size to 100% width and height in the config
          // helps the library manage the video element better.
          rememberLastUsedCamera: true,
          supportedScanTypes: [0, 1], // QR_CODE, BARCODE
        },
        (decodedText: string, _decodedResult: any) => {
          // Success callback
          this.onScanSuccess(decodedText);
        },
        (_errorMessage: string) => {
          // parse error, ignore it.
        }
      )
      .then(() => {
        // Success start
        this.statusMessage.set('Scanning...');
      })
      .catch((err: any) => {
        this.permissionError.set(`Start failed: ${err}`);
        this.statusMessage.set('Error');
        this.isCameraActive.set(false); // Reset state if start failed
      });
  }

  onScanSuccess(decodedText: string) {
    this.scanSuccess.emit(decodedText);
    this.scanResult.set(decodedText);
    this.statusMessage.set('Code Found!');
    // Optional: Stop scanning automatically
    this.stopScanner();
  }

  stopScanner() {
    this.devices = null;
    if (this.html5QrCode) {
      this.html5QrCode
        .stop()
        .then(() => {
          this.isCameraActive.set(false);
          this.statusMessage.set('Stopped');
          this.html5QrCode.clear();
        })
        .catch((err: any) => {
          console.error('Failed to stop', err);
        });
    }
  }

  onCameraChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    this.currentCameraId = select.value;

    // Restart with new camera
    if (this.isCameraActive()) {
      this.html5QrCode.stop().then(() => {
        this.startScanningWithId(this.currentCameraId!);
      });
    }
  }
}
